// Browser-based "Leave Now" push reminders.
// We schedule notifications on the client using setTimeout. Reminders are
// persisted in localStorage so they survive page reloads (re-armed on app load).

const REMINDERS_KEY = 'abp:reminders';
const _activeTimers = new Map(); // id -> timeout handle

function readAll() {
  try {
    const raw = localStorage.getItem(REMINDERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(list) {
  localStorage.setItem(REMINDERS_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent('abp:reminders-changed', { detail: list }));
}

export function isPushSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

export async function requestNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const result = await Notification.requestPermission();
  return result;
}

export function getReminders() {
  // Auto-prune past reminders
  const now = Date.now();
  const all = readAll().filter((r) => r.fireAt > now);
  if (all.length !== readAll().length) writeAll(all);
  return all;
}

export function subscribeReminders(callback) {
  const handler = () => callback(getReminders());
  window.addEventListener('abp:reminders-changed', handler);
  window.addEventListener('storage', (e) => {
    if (e.key === REMINDERS_KEY) handler();
  });
  return () => {
    window.removeEventListener('abp:reminders-changed', handler);
  };
}

function fireNotification(reminder) {
  if (!isPushSupported()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(`Time to leave for ${reminder.airportCode}`, {
      body: reminder.body || `Head to the airport now to arrive by ${reminder.arriveBy}.`,
      icon: '/favicon.ico',
      tag: `abp-${reminder.id}`,
      requireInteraction: false,
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch (e) {
    console.warn('Notification error:', e);
  }
}

function scheduleTimer(reminder) {
  const delay = Math.max(0, reminder.fireAt - Date.now());
  // setTimeout is limited to ~24.8 days (2^31 ms) - cap the delay
  const safeDelay = Math.min(delay, 2_000_000_000);
  const handle = setTimeout(() => {
    fireNotification(reminder);
    // Remove after firing
    const remaining = readAll().filter((r) => r.id !== reminder.id);
    writeAll(remaining);
    _activeTimers.delete(reminder.id);
  }, safeDelay);
  _activeTimers.set(reminder.id, handle);
}

export function scheduleReminder({ airportCode, arriveBy, fireAt, leadMinutes, body }) {
  const id = `${airportCode}-${fireAt}`;
  const reminder = {
    id,
    airportCode,
    arriveBy,
    fireAt,
    leadMinutes: leadMinutes || 30,
    body,
    createdAt: Date.now(),
  };
  // Replace any existing reminder for the same airport+slot
  const remaining = readAll().filter((r) => r.id !== id);
  remaining.push(reminder);
  writeAll(remaining);
  // Cancel and reschedule timer
  if (_activeTimers.has(id)) {
    clearTimeout(_activeTimers.get(id));
    _activeTimers.delete(id);
  }
  scheduleTimer(reminder);
  return reminder;
}

export function cancelReminder(id) {
  if (_activeTimers.has(id)) {
    clearTimeout(_activeTimers.get(id));
    _activeTimers.delete(id);
  }
  const remaining = readAll().filter((r) => r.id !== id);
  writeAll(remaining);
}

// Re-arm all stored reminders on app load
export function rehydrateReminders() {
  const now = Date.now();
  const all = readAll();
  const future = all.filter((r) => r.fireAt > now);
  if (future.length !== all.length) writeAll(future);
  future.forEach((r) => {
    if (!_activeTimers.has(r.id)) scheduleTimer(r);
  });
}

// Compute fireAt timestamp from "I:MM AM/PM" arrival string + a target date (YYYY-MM-DD)
// and a lead-time in minutes. Returns null if not parseable.
export function computeFireAt(arriveByStr, dateStr, leadMinutes = 30) {
  if (!arriveByStr || !dateStr) return null;
  const m = arriveByStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  let hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  const [y, mo, d] = dateStr.split('-').map(Number);
  // Use local browser time as approximation. Acceptable since user is local-time aware.
  const target = new Date(y, mo - 1, d, hours, minutes, 0);
  const fireAt = target.getTime() - leadMinutes * 60 * 1000;
  return fireAt;
}
